import { useCallback, useEffect, useRef } from 'react'

const TICK_MS = 1000
const IDLE_MS = 2 * 60 * 1000
const FLUSH_MS = 30 * 1000
const ACTIVITY_EVENTS = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'wheel']

// Counts time on task. A second only counts while the tab is visible and the
// learner has touched the page (mouse, keyboard, touch or scroll) in the
// last two minutes.
//
//   const { take, restore } = useActiveTime({ onFlush })
//
// `take()` hands over the whole seconds counted since the last take and
// resets the counter; `restore(seconds)` puts them back if the save failed.
// `onFlush` is called about every 30 seconds, when the tab is hidden, and on
// unmount, so the caller can send what has built up.
const useActiveTime = ({ onFlush } = {}) => {
  const activeMs = useRef(0)
  const lastTick = useRef(Date.now())
  const lastInteraction = useRef(Date.now())
  const lastTake = useRef(Date.now())
  const lastFlush = useRef(Date.now())
  const flushRef = useRef(onFlush)
  flushRef.current = onFlush

  const take = useCallback(() => {
    const now = Date.now()
    // Never claim more than the wall-clock time since the last take.
    const seconds = Math.max(
      0,
      Math.min(Math.floor(activeMs.current / 1000), Math.floor((now - lastTake.current) / 1000)),
    )
    if (seconds > 0) {
      activeMs.current = Math.min(activeMs.current - seconds * 1000, 999)
      lastTake.current = now
    }
    return seconds
  }, [])

  const restore = useCallback((seconds) => {
    if (!(seconds > 0)) return
    activeMs.current += seconds * 1000
    lastTake.current -= seconds * 1000
  }, [])

  useEffect(() => {
    const flush = () => {
      lastFlush.current = Date.now()
      flushRef.current?.()
    }
    const onActivity = () => {
      lastInteraction.current = Date.now()
    }
    const tick = () => {
      const now = Date.now()
      // A long gap between ticks means the device slept or the tab was
      // throttled; none of that gap counts.
      const delta = Math.min(now - lastTick.current, TICK_MS * 2)
      lastTick.current = now
      const visible = document.visibilityState === 'visible'
      if (visible && now - lastInteraction.current <= IDLE_MS) activeMs.current += delta
      if (now - lastFlush.current >= FLUSH_MS) flush()
    }
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') {
        flush()
      } else {
        lastTick.current = Date.now()
        lastInteraction.current = Date.now()
      }
    }

    lastTick.current = Date.now()
    const timer = setInterval(tick, TICK_MS)
    const options = { capture: true, passive: true }
    ACTIVITY_EVENTS.forEach((name) => window.addEventListener(name, onActivity, options))
    document.addEventListener('visibilitychange', onVisibility)
    return () => {
      clearInterval(timer)
      ACTIVITY_EVENTS.forEach((name) => window.removeEventListener(name, onActivity, options))
      document.removeEventListener('visibilitychange', onVisibility)
      flush()
    }
  }, [])

  return { take, restore }
}

export default useActiveTime
